// Communications data layer (Sotto-blocco 2).
// Cache in-memory idratata dal backend: identità mittente (sender_accounts) +
// template comunicazione per modulo (comm_templates, oggi solo calendar).
// API sincrone per i consumer; mutazioni ottimistiche + persistenza backend.
import { useSyncExternalStore } from "react";
import { api } from "@/lib/api";

let _senders = [];
let _templates = { calendar: [] };
let _version = 0;
const subs = new Set();
const notify = () => { _version += 1; subs.forEach((f) => f()); };

export const subscribeComm = (fn) => { subs.add(fn); return () => subs.delete(fn); };
export const useComm = () =>
  useSyncExternalStore((cb) => subscribeComm(cb), () => _version, () => _version);

export async function hydrateComm(perms = []) {
  try {
    _senders = await api.get("/communications/senders").then((r) => r.data);
  } catch { _senders = []; }
  if (perms.includes("view_calendar")) {
    try {
      _templates.calendar = await api
        .get("/communications/templates", { params: { module: "calendar" } })
        .then((r) => r.data);
    } catch { _templates.calendar = []; }
  }
  notify();
}

// Carica on-demand i template di un modulo specifico (support/administration/editorial/accounting).
export async function hydrateCommModule(module) {
  try {
    _templates[module] = await api
      .get("/communications/templates", { params: { module } })
      .then((r) => r.data);
  } catch { _templates[module] = []; }
  notify();
}

export function resetComm() {
  _senders = [];
  _templates = { calendar: [] };
  notify();
}

// ---- senders ----
export const listSenders = () => _senders;
export const sendersForModule = (m) =>
  _senders.filter((s) => s.active !== false && (!m || (s.available_for_modules || []).includes(m)));
export const senderName = (id) => _senders.find((s) => s.id === id)?.display_name || "";

export async function saveSender(data) {
  if (data.id) {
    _senders = _senders.map((s) => (s.id === data.id ? { ...s, ...data } : s));
    notify();
    await api.patch(`/communications/senders/${data.id}`, data);
  } else {
    const res = await api.post("/communications/senders", data);
    _senders = [..._senders, res.data];
    notify();
  }
}

export async function removeSender(id) {
  _senders = _senders.filter((s) => s.id !== id);
  notify();
  await api.delete(`/communications/senders/${id}`);
}

// ---- module templates (calendar) ----
export const listCommTemplates = (module) => _templates[module] || [];
export const commTemplatesForChannel = (module, channel) =>
  (_templates[module] || []).filter((t) => t.active !== false && (!channel || t.channel === channel));

export async function saveCommTemplate(module, data) {
  if (data.id) {
    _templates[module] = (_templates[module] || []).map((t) => (t.id === data.id ? { ...t, ...data } : t));
    notify();
    await api.patch(`/communications/templates/${data.id}`, data);
  } else {
    const res = await api.post("/communications/templates", { ...data, module });
    _templates[module] = [...(_templates[module] || []), res.data];
    notify();
  }
}

export async function removeCommTemplate(module, id) {
  _templates[module] = (_templates[module] || []).filter((t) => t.id !== id);
  notify();
  await api.delete(`/communications/templates/${id}`);
}

// Trigger/canale labels condivise (calendar).
export const COMM_CHANNELS = { email: "Email", whatsapp: "WhatsApp" };
export const COMM_CALENDAR_TRIGGERS = {
  appointment_confirmation: "Conferma appuntamento",
  appointment_reminder: "Reminder appuntamento",
  appointment_rescheduled: "Spostamento appuntamento",
  appointment_cancelled: "Annullamento appuntamento",
  generic: "Generico",
};
export const COMM_VARS = [
  "{{client_name}}", "{{contact_name}}", "{{appointment_title}}", "{{appointment_date}}",
  "{{appointment_time}}", "{{team_member_name}}", "{{meeting_link}}", "{{location}}",
];
export const REMINDER_TIMING_OPTIONS = {
  "1h": "1 ora prima", "3h": "3 ore prima", "24h": "24 ore prima",
  "48h": "48 ore prima", "custom": "Personalizzato",
};
